import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  applyGroupMemberRole,
  useAddMembersToGroupMutation,
  useChangeGroupUserRoleMutation,
  useDeleteGroupMutation,
  useGetUsersByGroupOptions,
  useRemoveMembersFromGroupMutation,
  useUpdateGroupNameMutation,
} from '@/hooks/queries/groups';
import { Trash2, Users, X, Trash, MoreVertical } from 'lucide-react';
import type { GroupDetails, GroupUserRole } from '@/types/groups';
import Loader from '../ui/Loader';
import { memo, useCallback, useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { useUser } from '@/hooks/queries/auth';
import type { UserDetails } from '@/types';
import { DropdownMenu, DropdownMenuItem, DropdownMenuContent, DropdownMenuTrigger } from '@ui/dropdown-menu';
import { dedupError, dedupSuccess, makeUpdateToastId } from '@/lib/toast';
import InviteUserByEmail from './InviteUserByEmail';
import { LegacyGroupUser } from '@/api/services/groups';
import { useConfirmHandler } from '@/hooks/useConfirmHandler';
import {
  getGroupRoleBadgeVariant,
  getGroupRoleLabel,
  GROUP_USER_ROLE_OPTIONS,
  resolveGroupUserRole,
} from '@/lib/groupRoles';

interface GroupDetailsDialogProps {
  group: GroupDetails | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Reusable dialog component for displaying group details and managing users
 */
export function GroupDetailsDialog({ group, open, onOpenChange }: GroupDetailsDialogProps) {
  const loggedInUser = useUser(state => state.user);
  const [memberQuery, setMemberQuery] = useState('');
  const [inviteRole, setInviteRole] = useState<GroupUserRole>('READER');
  const { mutateAsync: addMembersToGroup } = useAddMembersToGroupMutation();
  const {
    data: groupMembersData,
    isPending: isUsersLoading,
  } = useQuery(
    useGetUsersByGroupOptions(group?.id || '', open && !!group?.id, { includePending: true })
  );
  const groupMembers = (groupMembersData?.data as LegacyGroupUser)?.members;
  const pendingInvites = (groupMembersData?.data as LegacyGroupUser)?.pendingInvites;

  const canEditGroup = useMemo(() => {
    return groupMembers?.find((user) => String(user.user.id) === String(loggedInUser?.id))?.isAdmin;
  }, [groupMembers, loggedInUser]);

  const [groupName, setGroupName] = useState(group?.name);

  const filteredUsers = useMemo(() => {
    const query = memberQuery.trim().toLowerCase();
    if (!query) return groupMembers ?? [];
    return (groupMembers ?? []).filter(({ user }) => {
      const firstName = user.firstName?.toLowerCase() ?? '';
      const lastName = user.lastName?.toLowerCase() ?? '';
      const email = user.email?.toLowerCase() ?? '';
      return firstName.includes(query) || lastName.includes(query) || email.includes(query);
    });
  }, [memberQuery, groupMembers]);

  const { mutateAsync: updateGroupName, isPending: isUpdatingGroupName } = useUpdateGroupNameMutation();
  const { mutateAsync: deleteGroup, isPending: isDeletingGroup } = useDeleteGroupMutation();
  const confirm = useConfirmHandler();

  const handleSaveGroupName = useCallback(async () => {
    if (isUpdatingGroupName) return;
    const newName = groupName?.trim();
    if (newName && newName !== group?.name?.trim()) {
      try {
        const updatedGroup = await updateGroupName({
          groupId: String(group?.id),
          name: newName,
        });
        const toastId = makeUpdateToastId('group', group?.id || 'unknown', 'name');
        if (updatedGroup.status) {
          setGroupName(updatedGroup.data.name);
          dedupSuccess(toastId, 'Group name updated');
        } else {
          dedupError(toastId, 'Failed to update group name');
        }
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Failed to update group name';
        dedupError(makeUpdateToastId('group', group?.id || 'unknown', 'name'), message);
      }
    }
  }, [groupName, group, updateGroupName, isUpdatingGroupName]);

  const handleDeleteGroup = useCallback(async () => {
    if (!group?.id || isDeletingGroup) return;

    const shouldDelete = await confirm({
      title: 'Delete group',
      description: `Are you sure you want to delete "${group.name}"? This action cannot be undone.`,
      variant: 'destructive',
      confirmText: 'Delete',
      cancelText: 'Cancel',
    });

    if (!shouldDelete) return;

    try {
      const response = await deleteGroup(String(group.id));
      if (response.status) {
        toast.success(response.data.message || 'Group deleted successfully');
        onOpenChange(false);
      }
    } catch (error: unknown) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'Failed to delete group';
      toast.error(message);
    }
  }, [confirm, deleteGroup, group?.id, group?.name, isDeletingGroup, onOpenChange]);

  const handleKnownUserInvite = useCallback(
    async (targetUser: UserDetails) => {
      const isUserAlreadyInGroup = (groupMembers ?? []).some(
        ({ user }) => String(user.id) === String(targetUser.id)
      );
      if (isUserAlreadyInGroup) {
        toast.error(`User ${targetUser.email} is already in the group`);
        return;
      }

      const groupId = String(group?.id);
      await addMembersToGroup({ groupId, userIds: [targetUser.id] }).catch((error) => {
        throw new Error(error?.message || `Failed to add user ${targetUser.email}`);
      });
      await applyGroupMemberRole(groupId, targetUser.id, inviteRole);
      setMemberQuery('');
    },
    [addMembersToGroup, group?.id, groupMembers, inviteRole]
  );

  if (!group) return null;

  const filteredUsersLength = filteredUsers?.length ?? 0;
  const pendingInvitesLength = pendingInvites?.length ?? 0;
  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          setInviteRole('READER');
          setMemberQuery('');
        }
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent
        className="max-w-2xl"
        showCloseButton={false}
        aria-labelledby="group-details-title"
        aria-describedby="group-details-description"
      >
        <DialogHeader>
          <DialogTitle
            id="group-details-title"
            className="flex items-center justify-between gap-2"
          >
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5" aria-hidden="true" />
              <span className="text-lg font-medium">Group Details</span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onOpenChange(false)}
              type="button"
              aria-label="Close group details dialog"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </Button>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4" id="group-details-description">
          <section aria-labelledby="group-info-heading">
            <h3 id="group-info-heading" className="text-muted-foreground text-sm font-medium">Group Information</h3>
            <div className={cn(canEditGroup ? 'py-2' : 'rounded-lg border p-3')}>
              {canEditGroup ? (
                <div className="space-y-2">
                  <label htmlFor="group-name-input" className="text-sm font-bold">Name</label>
                  <Input
                    id="group-name-input"
                    defaultValue={group.name}
                    onChange={(e) => setGroupName(e.target.value)}
                    onBlur={handleSaveGroupName}
                    containerClassName="py-0.5 px-1"
                    aria-describedby="group-name-help"
                    endIcon={
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleSaveGroupName}
                        type="button"
                        disabled={
                          isUpdatingGroupName ||
                          !groupName?.trim() ||
                          groupName?.trim() === group?.name?.trim()
                        }
                        className="bg-primary/10 w-auto"
                        aria-label={isUpdatingGroupName ? "Saving group name..." : "Save group name"}
                      >
                        {isUpdatingGroupName ? (
                          <Loader variant="dark" size={20} aria-hidden="true" />
                        ) : (
                          <span className="px-4 text-sm">Save</span>
                        )}
                      </Button>
                    }
                  />
                  <div id="group-name-help" className="sr-only">
                    Press Enter or click Save to update the group name
                  </div>
                </div>
              ) : (
                <p className="text-sm">
                  <strong>Name:</strong> {group.name}
                </p>
              )}
            </div>
          </section>

          <Separator />

          <section aria-labelledby="members-heading">
            <div className="flex items-center justify-between">
              <h3 id="members-heading" className="text-muted-foreground text-sm font-medium">
                Members {filteredUsersLength ? `(${filteredUsersLength})` : ''}
              </h3>
            </div>

            {canEditGroup && (
              <InviteUserByEmail
                groupId={group.id}
                value={memberQuery}
                onValueChange={setMemberQuery}
                onKnownUser={handleKnownUserInvite}
                inviteRole={inviteRole}
                onInviteRoleChange={setInviteRole}
              />
            )}

            {isUsersLoading ? (
              <div
                className="flex items-center justify-center py-4"
                aria-live="polite"
                aria-label="Loading group members"
              >
                <Loader variant="dark" size={20} aria-hidden="true" />
                <span className="sr-only">Loading group members...</span>
              </div>
            ) : (filteredUsersLength > 0 || pendingInvitesLength > 0) ? (
              <div
                className="max-h-64 space-y-2 overflow-y-auto rounded-lg py-2"
                role="list"
                aria-label="Group members list"
              >
                {filteredUsersLength > 0 && (
                  <div className="space-y-2">
                    {filteredUsers.map((member) => (
                      <GroupDetailsDialog.MemberItem
                        key={member.user.id}
                        user={member.user}
                        role={resolveGroupUserRole(member)}
                        isCurrentUserAdmin={Boolean(canEditGroup)}
                        groupId={group.id}
                        isMe={String(member.user.id) === String(loggedInUser?.id)}
                      />
                    ))}
                  </div>
                )}
                {pendingInvitesLength > 0 && (
                  <div className="space-y-2">
                    <p className="text-muted-foreground text-xs font-medium">Pending Invites ({pendingInvitesLength})</p>
                    {pendingInvites?.map(({ inviteeUserEmail, groupId }) => (
                      <GroupDetailsDialog.PendingInviteItem
                        key={inviteeUserEmail}
                        email={inviteeUserEmail as string}
                        groupId={String(groupId)}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div
                className="text-muted-foreground py-8 text-center"
                aria-live="polite"
              >
                <Users className="mx-auto mb-2 h-12 w-12 opacity-50" aria-hidden="true" />
                <p>No members found in this group</p>
              </div>
            )}
          </section>
        </div>
        {canEditGroup ? (
          <DialogFooter className="border-t pt-4 sm:justify-start">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                void handleDeleteGroup();
              }}
              disabled={isDeletingGroup}
              className="border-error-E-main/30 text-error-E-dark hover:bg-error-E-main/10 hover:text-error-E-dark"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete group
            </Button>
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Member row with role badge or role selector for group admins.
 */
GroupDetailsDialog.MemberItem = memo(
  ({
    user,
    role,
    isCurrentUserAdmin,
    groupId,
    isMe,
    readOnly = false,
  }: {
    user: UserDetails;
    role: GroupUserRole;
    isCurrentUserAdmin: boolean;
    groupId: string;
    isMe: boolean;
    readOnly?: boolean;
  }) => {
    const { mutateAsync: changeGroupUserRole, isPending: isChangingRole } =
      useChangeGroupUserRoleMutation();
    const { mutateAsync: removeMembersFromGroup } = useRemoveMembersFromGroupMutation();

    const handleRoleChange = useCallback(
      async (nextRole: GroupUserRole) => {
        if (nextRole === role) return;
        try {
          await changeGroupUserRole({
            groupId: String(groupId),
            userId: user.id,
            role: nextRole,
          });
        } catch {
          // Mutation shows toast on error.
        }
      },
      [changeGroupUserRole, groupId, role, user.id],
    );

    const handleRemoveMember = useCallback(async () => {
      const updatedGroup = await removeMembersFromGroup({
        groupId: String(groupId),
        userIds: [user.id],
      });
      if (updatedGroup.status) {
        toast.success('Member removed from group successfully');
      }
    }, [groupId, removeMembersFromGroup, user.id]);

    const roleLabel = getGroupRoleLabel(role);

    return (
      <div
        className="flex items-center justify-between rounded-md border p-2"
        role="listitem"
        aria-label={`${isMe ? 'You' : user.firstName || 'Unknown'} - ${user.email} - ${roleLabel}`}
      >
        <div className="flex items-center gap-3">
          <div
            className="bg-primary/10 flex h-8 w-8 items-center justify-center rounded-full"
            aria-hidden="true"
          >
            <span
              className={cn(
                'text-xs font-medium',
                (!user.isEnabled || user.isBlocked) && 'line-through'
              )}
            >
              {user.firstName?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <p className="text-sm font-medium">{isMe ? 'You' : user.firstName || 'Unknown'}</p>
            <p className="text-muted-foreground text-xs">{user.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isCurrentUserAdmin && !isMe && !readOnly ? (
            <Select
              value={role}
              onValueChange={(nextRole) => void handleRoleChange(nextRole as GroupUserRole)}
              disabled={isChangingRole}
            >
              <SelectTrigger size="sm" className="w-[110px]" aria-label={`Role for ${user.email}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GROUP_USER_ROLE_OPTIONS.map(({ value: roleValue, label }) => (
                  <SelectItem key={roleValue} value={roleValue}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Badge
              variant={getGroupRoleBadgeVariant(role)}
              aria-label={`User role: ${roleLabel}`}
            >
              {roleLabel}
            </Badge>
          )}
          {isCurrentUserAdmin && !isMe && !readOnly && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="w-auto px-2"
                  aria-label={`Manage ${user.firstName || user.email} options`}
                >
                  <MoreVertical className="h-4 w-4" aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem
                  onClick={handleRemoveMember}
                  aria-label={`Remove ${user.firstName || user.email} from group`}
                >
                  <Trash className="h-4 w-4" aria-hidden="true" />
                  Remove from group
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    );
  }
);

GroupDetailsDialog.PendingInviteItem = memo(
  ({ email }: { email: string; groupId: string }) => {
    return (
      <div className="flex items-center justify-between rounded-md border p-2" role="listitem">
        <div className="flex items-center gap-3">
          <div className="bg-primary/10 flex h-8 w-8 items-center justify-center rounded-full" aria-hidden="true">
            <span className="text-xs font-medium">{email?.charAt(0).toUpperCase()}</span>
          </div>
          <div>
            <p className="text-sm font-medium">Invited User</p>
            <p className="text-muted-foreground text-xs">{email}</p>
          </div>
        </div>
        <Badge variant="outline">Invited</Badge>
      </div>
    );
  }
);
